import { useCallback, useState } from 'react';
import { ActionItem } from '../components/StepActions';
import { HTMLService } from '../services/htmlService';
import { StepSummaryContext } from '../services/stepSummaryHtml';
import { DEFAULT_SHARE_EMPTY_MESSAGE, getStepShareMeta } from '../config/stepShareConfig';
import { canShareStepWork } from '../utils/stepShareUtils';

export function useStepShareDialog(
  stepNumber: number,
  user: { name?: string; display_name?: string; email?: string } | undefined,
  actions: ActionItem[],
  getExtraContext: () => Omit<StepSummaryContext, 'stepNumber' | 'user' | 'actions'>
) {
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const meta = getStepShareMeta(stepNumber);

  const hasExtraData = useCallback(() => {
    const extra = getExtraContext();
    switch (stepNumber) {
      case 2:
        return Object.keys(extra.questionAnswers || {}).some((k) => extra.questionAnswers![k]?.answer?.trim());
      case 3:
        return (
          !!extra.prayerText?.trim() ||
          Object.keys(extra.questionAnswers || {}).some((k) => extra.questionAnswers![k]?.answer?.trim())
        );
      case 4:
        return (
          (extra.resentments?.length || 0) > 0 ||
          (extra.fears?.length || 0) > 0 ||
          (extra.harms?.length || 0) > 0
        );
      case 5:
        return (
          !!(
            extra.step5InventoryPresentation?.startedDate?.trim() ||
            extra.step5InventoryPresentation?.place?.trim() ||
            extra.step5InventoryPresentation?.reflection?.trim()
          ) || (extra.characterDefects?.length || 0) > 0
        );
      case 6:
        return (
          (extra.step6Defects?.length || 0) > 0 || Object.keys(extra.step6DefectAnswers || {}).length > 0
        );
      case 7:
        return !!extra.step7Prayer?.trim();
      case 8:
      case 9:
        return (extra.amendsPersons?.length || 0) > 0;
      case 10:
        return (extra.dailyInventories?.length || 0) > 0;
      case 11:
        return !!extra.quietTimePlan?.trim() || !!extra.quietTimePracticed3Days;
      case 12:
        return !!extra.servicePlan?.trim();
      case 1:
        return !!(
          extra.step1Writing?.hasContent ||
          extra.step1Writing?.externalNote?.trim() ||
          (extra.step1Writing?.wordCount && extra.step1Writing.wordCount > 0)
        );
      default:
        return false;
    }
  }, [getExtraContext, stepNumber]);

  const canShare = useCallback(
    () => canShareStepWork(stepNumber, actions, hasExtraData()),
    [stepNumber, actions, hasExtraData]
  );

  const buildShareHtml = useCallback(() => {
    const extra = getExtraContext();
    return HTMLService.generateStepSummaryHTML({
      stepNumber,
      user: { name: user?.name || user?.display_name, email: user?.email },
      actions: actions.map(({ id, completed }) => ({ id, completed })),
      ...extra,
    });
  }, [stepNumber, user, actions, getExtraContext]);

  return {
    isShareDialogOpen,
    openShareDialog: () => setIsShareDialogOpen(true),
    closeShareDialog: () => setIsShareDialogOpen(false),
    canShare,
    buildShareHtml,
    shareDialogProps: {
      isOpen: isShareDialogOpen,
      onClose: () => setIsShareDialogOpen(false),
      user,
      stepTitle: meta.stepTitle,
      shareName: meta.shareName,
      documentType: meta.documentType,
      generateHtml: buildShareHtml,
      canGenerate: canShare,
      emptyMessage: DEFAULT_SHARE_EMPTY_MESSAGE,
    },
  };
}
